import { acceptHMRUpdate, defineStore } from "pinia";
import { ref } from "vue";

import type { IStartPricePerMinuteTimerCmd } from "@/data/commands/customer/StartPricePerMinuteTimerCmd";
import type { IStopPricePerMinuteTimerCmd } from "@/data/commands/customer/StopPricePerMinuteTimerCmd";
import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import { dataEvents } from "@/data/events/dataEvents";
import type { IBillEvent } from "@/data/model/Events";
import { useTableAllocationStore } from "@/data/stores/tableAllocation";

import { logError } from "@/shared/logging/errors";

// the articles with a running timer at the table of the current user. it belongs to the table, so it is not part of
// the offer, which is the same for every table
export const useTimersStore = defineStore("timers", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    const runningArticleIds = ref(new Set<string>());
    // while a payment is being made no timer can be started or resumed, it would change the bill that is being paid
    const isStartBlocked = ref(false);
    let pendingLoad: Promise<void> | undefined;
    let hasLoaded = false;
    let reloadAfterPendingLoad = false;

    // a timer is also stopped or started elsewhere: by a payment, a transfer, a cancellation, the end of the offer or on
    // another device. each of these changes a bill of the table, which may be another one than the operator's own, an
    // order bill of the table say: the timers run on all of them, so the event of any bill of the table loads them again
    const allocation = useTableAllocationStore();
    function onBillEvent(ev: IBillEvent) {
        if (!hasLoaded || ev.tableId !== allocation.tableId) return;

        // a change during a load may not be in its answer, so one more load follows it
        if (pendingLoad !== undefined) {
            reloadAfterPendingLoad = true;
            return;
        }

        load();
    }
    dataEvents.appSocket.BillOpened.add(onBillEvent);
    dataEvents.appSocket.BillChangedShared.add(onBillEvent);
    dataEvents.appSocket.BillClosed.add(onBillEvent);

    function isRunning(articleId: string): boolean {
        return runningArticleIds.value.has(articleId);
    }

    function load(): Promise<void> {
        pendingLoad ??= customerDao
            .getRunningTimers()
            .then((articleIds) => {
                runningArticleIds.value = new Set(articleIds);
                hasLoaded = true;
            })
            .catch((ex) => logError(ex))
            .finally(() => {
                pendingLoad = undefined;

                if (reloadAfterPendingLoad) {
                    reloadAfterPendingLoad = false;
                    load();
                }
            });

        return pendingLoad;
    }

    async function start(cmd: IStartPricePerMinuteTimerCmd): Promise<void> {
        if (isStartBlocked.value) return;

        await customerDao.startPricePerMinuteTimer(cmd);
        await load();
    }

    async function stop(cmd: IStopPricePerMinuteTimerCmd): Promise<void> {
        await customerDao.stopPricePerMinuteTimer(cmd);
        await load();
    }

    return { isStartBlocked, isRunning, load, start, stop };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTimersStore, import.meta.hot));
}
